/**
 * Shared API response transformation utilities.
 *
 * Provides reusable functions for transforming snake_case backend
 * responses to frontend types, reducing duplication across services.
 */

/**
 * Safely convert a backend value to a number.
 * Handles string decimals, null, and undefined.
 *
 * @param value - The value to convert
 * @param defaultValue - Default value if conversion fails (default: 0)
 * @returns The numeric value
 */
export function toNumber(value: unknown, defaultValue = 0): number {
  if (value === null || value === undefined) return defaultValue;
  const num = Number(value);
  return isNaN(num) ? defaultValue : num;
}

/**
 * Safely convert a backend value to a string.
 *
 * @param value - The value to convert
 * @param defaultValue - Default value if null/undefined (default: '')
 * @returns The string value
 */
export function toStr(value: unknown, defaultValue = ''): string {
  if (value === null || value === undefined) return defaultValue;
  return String(value);
}

/**
 * Safely convert a backend value to a boolean.
 *
 * @param value - The value to convert
 * @param defaultValue - Default value if null/undefined (default: false)
 * @returns The boolean value
 */
export function toBool(value: unknown, defaultValue = false): boolean {
  if (value === null || value === undefined) return defaultValue;
  return Boolean(value);
}

/**
 * Transform a record using a mapping of target keys to source keys/transforms.
 * Reduces boilerplate in service files.
 *
 * @param source - The source record from the API
 * @param mapping - Object where keys are target field names and values are source field names
 * @param numberFields - Fields that should be converted to numbers
 * @returns Transformed object
 */
export function transformRecord<T>(
  source: Record<string, unknown>,
  mapping: Record<string, string>,
  numberFields: string[] = []
): T {
  const result: Record<string, unknown> = {};

  for (const [targetKey, sourceKey] of Object.entries(mapping)) {
    const value = source[sourceKey];
    if (numberFields.includes(targetKey)) {
      result[targetKey] = toNumber(value);
    } else {
      result[targetKey] = value ?? '';
    }
  }

  return result as T;
}
